import { THDefaultDaypartHours } from '../components/OperationHoursTab/constants/THDefaultDaypartHours';
import _ from 'lodash';
import { parse } from 'date-fns';
import { ServiceTime, UpdateDaypartHoursParams } from '../interfaces/daypart';

const MAX_TIME = '23:59';
const YES_FLAG = 'Y';

export function getAutoCalculatedTHDaypartHours(
  serviceTime: ServiceTime[],
): Record<string, Record<string, string>> {
  const autoCalculatedTHDaypartHours = _.cloneDeep(THDefaultDaypartHours);

  // If all service times are closed, return empty daypart hours
  if (
    serviceTime.length > 0 &&
    serviceTime.every(time => time.isClosed === YES_FLAG)
  ) {
    Object.values(autoCalculatedTHDaypartHours).forEach(daypartHours => {
      Object.keys(daypartHours).forEach(timeKey => {
        daypartHours[timeKey] = '';
      });
    });
    return autoCalculatedTHDaypartHours;
  }
  const { openTimes, closeTimes } = extractServiceTimes(serviceTime);

  // If no valid times provided at all, return default hours
  if (openTimes.length === 0 || closeTimes.length === 0) {
    return THDefaultDaypartHours;
  }

  const extremeTimeOpen = findExtremeTime(openTimes, true);
  const extremeTimeClose = findExtremeTime(closeTimes, false);

  Object.values(autoCalculatedTHDaypartHours).forEach(daypartHours => {
    updateAutoCalculatedTHDaypartHours({
      extremeTimeOpen,
      extremeTimeClose,
      daypartHours,
    });
  });
  return autoCalculatedTHDaypartHours;
}

function updateAutoCalculatedTHDaypartHours({
  extremeTimeOpen,
  extremeTimeClose,
  daypartHours,
}: UpdateDaypartHoursParams): void {
  const [daypartOpenKey, daypartCloseKey] = extractDaypartKeys(daypartHours);
  const daypartOpenTime = daypartHours[daypartOpenKey];
  const daypartCloseTime = daypartHours[daypartCloseKey];

  // Setting daypart hours to empty if service time is outside of daypart default hours
  if (compareTime(extremeTimeClose, daypartOpenTime) <= 0) {
    daypartHours[daypartOpenKey] = '';
    daypartHours[daypartCloseKey] = '';
    return;
  }
  if (compareTime(extremeTimeOpen, daypartCloseTime) >= 0) {
    daypartHours[daypartOpenKey] = '';
    daypartHours[daypartCloseKey] = '';
    return;
  }
  if (
    shouldUpdate({
      extremeTime: extremeTimeOpen,
      daypartTime: daypartOpenTime,
      isEarliest: true,
    })
  ) {
    daypartHours[daypartOpenKey] = extremeTimeOpen;
  }
  if (
    shouldUpdate({
      extremeTime: extremeTimeClose,
      daypartTime: daypartCloseTime,
      isEarliest: false,
    })
  ) {
    daypartHours[daypartCloseKey] = extremeTimeClose;
  }
}

// Returns earliest time when isEarliest=true, latest time when isEarliest=false
function findExtremeTime(times: string[], isEarliest: boolean): string {
  return times.reduce((extremeTime, time) => {
    const comparison = compareTime(time, extremeTime);
    return (isEarliest ? comparison < 0 : comparison > 0) ? time : extremeTime;
  });
}

function shouldUpdate({
  extremeTime,
  daypartTime,
  isEarliest,
}: {
  extremeTime: string;
  daypartTime: string;
  isEarliest: boolean;
}): boolean {
  if (!extremeTime || !daypartTime) return false;
  const comparison = compareTime(daypartTime, extremeTime);
  return isEarliest ? comparison < 0 : comparison > 0;
}

function compareTime(time1: string, time2: string): number {
  const date1 = parseTimeString(time1);
  const date2 = parseTimeString(time2);

  if (!date1 || !date2) return 0;

  return date1.getTime() - date2.getTime();
}

function extractDaypartKeys(
  daypartHours: Record<string, string>,
): [string, string] {
  const openKey = Object.keys(daypartHours).find(key => key.includes('Open'));
  const closeKey = Object.keys(daypartHours).find(key => key.includes('Close'));
  return [openKey || '', closeKey || ''];
}

function extractServiceTimes(serviceTime: ServiceTime[]): {
  openTimes: string[];
  closeTimes: string[];
} {
  const openTimes: string[] = [];
  const closeTimes: string[] = [];

  serviceTime.forEach(time => {
    if ([time.isOpened24Hours, time.isClosed].includes(YES_FLAG)) return;
    if (!isValidTime(time.open) || !isValidTime(time.close)) return;
    openTimes.push(time.open);
    // In case restaurant is closing next day, set close time to 23:59 to calculate daypart hours correctly
    const closeTime =
      compareTime(time.open, time.close) > 0 ? MAX_TIME : time.close;
    closeTimes.push(closeTime);
  });

  return { openTimes, closeTimes };
}

function isValidTime(time: string): boolean {
  return parseTimeString(time) !== null;
}

function parseTimeString(time: string): Date | null {
  if (!time) return null;

  try {
    const referenceDate = new Date(2000, 0, 1);
    const date = parse(time, 'HH:mm', referenceDate);

    if (isNaN(date.getTime())) return null;

    return date;
  } catch {
    return null;
  }
}
